import { defineStore } from 'pinia';
import Auth from '@/services/auth';
import { normalizeError } from '@/utils/error-handler';

export const useLoginStore = defineStore('login', {
    state: () => ({
        errors: [],
        formErrors: {},
        saving: false,
        loading: false,
        loginCreds: { email: '', password: '' },
        showPassword: false,
        show2FAModal: false,
        showTwoFactor: false,
        showResendCode: false,
        sendingCode: false,
        resending: false,
        code: '',
        resendCodeMessage: 'Resending code ...',
        intended: '',
        user: {},
        otpCode: '',
        showRecoveryCode: false,
        recoveryCode: null,
        phone: '',
        mode: 'login',
        intended: '',
        utmParams: {},
    }),
    actions: {
        login() {
            this.formErrors = {};
            this.errors = [];
            this.loading = true;
            axios.get('/sanctum/csrf-cookie').then(() => {
                axios
                    .post('/login', this.loginCreds)
                    .then((response) => {
                        if (response.status === 200) {
                            this.intended = response.data?.intended;
                            this.user = response.data;
                            if (response.data?.uses_2fa) {
                                this.show2FAModal = true;
                                this.loading = false;
                                this.phone = response.data.phone;
                                return false;
                            }

                            if (response.data?.two_factor) {
                                this.loading = false;
                                this.showTwoFactor = true;
                                return false;
                            }

                            if (import.meta.env.VITE_APP_ENV === 'production') {
                                woopra.identify({
                                    email: response.data.email,
                                    name: response.data.name,
                                    uuid: response.data.uuid,
                                });
                                woopra.track('login-my10x', {
                                    name: response.data.name,
                                    email: response.data.email,
                                    uuid: response.data.uuid,
                                });
                            }
                            if (response.data) {
                                localStorage.setItem(
                                    '_token',
                                    response.data.token
                                );
                                localStorage.setItem(
                                    'uuid',
                                    response.data.uuid
                                );
                                location.href = this.intended;
                            }
                        } else {
                            this.loading = false;
                            this.handleHttpStatus(response.response);
                        }
                    })
                    .catch((e) => {
                        console.log(e);
                        this.loading = false;
                        this.handleHttpStatus(e.response);
                    });
            });
        },
        verifyCode() {
            this.loading = true;
            const data = this.showRecoveryCode
                ? { recovery_code: this.recoveryCode }
                : { code: this.otpCode };

            const auth = Auth.twoFactorChallenge(data);
            auth.then((response) => {
                this.loading = false;

                if (import.meta.env.VITE_APP_ENV === 'production') {
                    woopra.identify({
                        email: this.user.email,
                        name: this.user.name,
                        uuid: this.user.uuid,
                    });
                    woopra.track('login-my10x', {
                        name: this.user.name,
                        email: this.user.email,
                        uuid: this.user.uuid,
                    });
                }
                localStorage.setItem('_token', response.data.token);
                localStorage.setItem('uuid', response.data.uuid);
                location.href = this.intended;
            }).catch((e) => {
                this.loading = false;
                this.handleHttpStatus(e.response);
            });
        },
        submitCode() {
            this.showResendCode = false;
            this.resending = false;
            this.resendCodeMessage = 'Resending code ...';
            this.errors = [];
            this.sendingCode = true;
            this.validateCode();
            if (this.code.toString().length === 6) {
                const auth = Auth.submitTwoFA({
                    code: this.code,
                });
                auth.then((response) => {
                    if (!response.data.valid_code) {
                        let message =
                            'The code is not valid. You may resend it.';
                        if (response.data.message === 'please_wait') {
                            message =
                                'Too many attempts. Please wait 5 min before trying again.';
                        }
                        if (response.data.message === 'too_many_tries') {
                            message =
                                'Account is locked. Too many failed attempts.';
                        }
                        this.errors.push({
                            message: message,
                        });
                        this.showResendCode = true;
                        this.sendingCode = false;
                    } else {
                        if (import.meta.env.VITE_APP_ENV === 'production') {
                            woopra.identify({
                                email: this.user.email,
                                name: this.user.name,
                                uuid: this.user.uuid,
                            });
                        }
                        if (this.mode === 'register') {
                            if (import.meta.env.VITE_APP_ENV === 'production') {
                                woopra.track('register-my10x', {
                                    name: this.user.name,
                                    email: this.user.email,
                                    uuid: this.user.uuid,
                                });
                            }
                            location.href = '/welcome';
                        } else if (this.mode === 'login') {
                            if (import.meta.env.VITE_APP_ENV === 'production') {
                                woopra.track('login-my10x', {
                                    name: this.user.name,
                                    email: this.user.email,
                                    uuid: this.user.uuid,
                                });
                            }
                            if (response.data) {
                                localStorage.setItem(
                                    '_token',
                                    response.data.token
                                );
                                location.href = this.intended;
                            }
                        }
                    }
                }).catch((e) => {
                    this.handleHttpStatus(e.response);
                    this.sendingCode = false;
                });
            }
        },
        resendCode() {
            this.resending = true;
            const auth = Auth.resendTwoFA();
            auth.then((response) => {
                if (!response.data.success) {
                    let message = 'The code is not valid. You may resend it.';
                    if (response.data.message === 'please_wait') {
                        message =
                            'Too many attempts. Please wait 5 min before trying again.';
                    }
                    if (response.data.message === 'too_many_tries') {
                        message =
                            'Account is locked. Too many failed attempts.';
                    }
                    this.code = '';
                    this.errors.push({
                        message: message,
                    });
                } else {
                    this.code = '';
                    this.resendCodeMessage = 'Code resent.';
                    this.errors = [];
                }
            }).catch((e) => {
                this.handleHttpStatus(e.response);
            });
        },
        validatePhoneNumber() {
            this.errors = [];
            this.validateCode();
            if (this.code.toString().length !== 6) {
                if (this.code.toString().length === 6) {
                    const auth = Auth.validatePhoneNumber({ code: this.code });
                    auth.then((response) => {
                        if (!response.data.valid_code) {
                            this.errors.push({
                                message:
                                    'The code is invalid or expired. You may resend it.',
                            });
                            this.showResendCode = true;
                            this.sendingCode = false;
                        } else {
                            //localStorage.setItem('_token', response.data.token);
                            //location.href = '/dashboard';
                        }
                    }).catch((e) => {
                        this.handleHttpStatus(e.response);
                        this.sendingCode = false;
                    });
                }
            }
        },
        validateCode() {
            if (this.code.toString().length !== 6) {
                this.errors.push({
                    message: 'The SMS code is invalid.',
                });
            }
        },
        checkLength() {
            if (this.code.toString().length > 6) {
                this.code = this.code.toString().slice(0, 6);
            }
        },
        handleHttpStatus(error) {
            if (!error) return;

            const response = normalizeError(error);
            this.formErrors = {};

            // Handle validation errors
            if (
                response.status === 422 &&
                response.data &&
                response.data.errors
            ) {
                for (const key in response.data.errors) {
                    this.errors.push({ message: response.data.errors[key][0] });
                    this.formErrors[key] = response.data.errors[key][0];
                }
            }

            // Handle authentication errors
            if (response.status === 401 || response.status === 403) {
                location.href = '/login?loginstatus=autologout';
            }
        },
        cancelSms() {
            this.show2FAModal = false;
        },
        cancelTotp() {
            this.showTwoFactor = false;
        },
        gotoTwoFactor() {
            this.showTwoFactor = true;
            this.showRecoveryCode = false;
        },
        close2FAModal() {
            this.show2FAModal = false;
        },
        setUtmParams(params) {
            this.utmParams = params;
            ApiService.apiCall('/utm-params', 'post', params);
        },
    },
});
